import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { HelpCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { AssetsTable } from "@/components/assets/AssetsTable";
import { AssetsEmptyState } from "@/components/assets/AssetsEmptyState";
import { BulkActionsToolbar } from "@/components/assets/BulkActionsToolbar";
import type { UnifiedAsset } from "@/hooks/useAssetsUnified";

interface AssetsTableContainerProps {
  // Data state
  isLoading: boolean;
  totalAssetCount: number;
  filteredAssetsCount: number;
  totalFilteredCount: number;
  allAssets?: UnifiedAsset[];
  paginatedAssets: UnifiedAsset[];
  // Table props
  sortConfig: any;
  onSort: (config: any) => void;
  selectedAssets: string[];
  onSelectionChange: (ids: string[]) => void;
  onEdit: (asset: UnifiedAsset) => void;
  onViewDetails?: (asset: UnifiedAsset) => void;
  onAssetAdded?: () => void;
  onRowDelete?: (asset: UnifiedAsset) => void;
  onAddAsset: () => void;
  onClearFilters: () => void;
  // Bulk actions
  onClearSelection: () => void;
  onAddToPortfolio: () => void;
  onBulkExport: () => void;
  onBulkDelete: () => void;
  onSelectAllFiltered: () => void;
  // Pagination
  pagination: { page: number; pageSize: number };
  totalPages: number;
  goToPage: (page: number) => void;
  changePageSize: (size: number) => void;
  // Empty state actions
  onAiImport?: () => void;
}

export function AssetsTableContainer({
  isLoading,
  totalAssetCount,
  filteredAssetsCount,
  totalFilteredCount,
  allAssets,
  paginatedAssets,
  sortConfig,
  onSort,
  selectedAssets,
  onSelectionChange,
  onEdit,
  onViewDetails,
  onAssetAdded,
  onRowDelete,
  onAddAsset,
  onClearFilters,
  onClearSelection,
  onAddToPortfolio,
  onBulkExport,
  onBulkDelete,
  onSelectAllFiltered,
  pagination,
  totalPages,
  goToPage,
  changePageSize,
  onAiImport,
}: AssetsTableContainerProps) {
  const [pageInputValue, setPageInputValue] = useState<string>(String(pagination.page));

  // keep input in sync if parent changes page
  React.useEffect(() => {
    setPageInputValue(String(pagination.page));
  }, [pagination.page]);

  return (
    <div className="space-y-4 mr-auto w-full max-w-[960px]">
      <BulkActionsToolbar
        selectedCount={selectedAssets?.length || 0}
        onClearSelection={onClearSelection}
        onAddToPortfolio={onAddToPortfolio}
        onBulkExport={onBulkExport}
        onBulkDelete={onBulkDelete}
        canDelete={(selectedAssets?.length || 0) > 0}
        onSelectAllFiltered={onSelectAllFiltered}
        totalFilteredCount={totalFilteredCount}
      />

      {isLoading ? (
        <AssetsTable
          assets={[]}
          sortConfig={sortConfig}
          onSort={onSort}
          onEdit={onEdit}
          selectedAssets={selectedAssets}
          onSelectionChange={onSelectionChange}
          isLoading={true}
        />
      ) : totalAssetCount === 0 ? (
        <div className="mt-6">
          <AssetsEmptyState type="no-assets" onAddAsset={onAddAsset} onAiImport={onAiImport} />
        </div>
      ) : filteredAssetsCount === 0 ? (
        <>
          <AssetsTable
            assets={[]}
            sortConfig={sortConfig}
            onSort={onSort}
            onEdit={onEdit}
            selectedAssets={selectedAssets}
            onSelectionChange={onSelectionChange}
            onDelete={onRowDelete}
          />
          <div className="mt-4">
            <AssetsEmptyState
              type="no-search-results"
              onAddAsset={onAddAsset}
              onClearFilters={onClearFilters}
            />
          </div>
        </>
      ) : (
        <>
          <AssetsTable
            assets={paginatedAssets}
            allAssets={allAssets}
            sortConfig={sortConfig}
            onSort={onSort}
            onEdit={onEdit}
            selectedAssets={selectedAssets}
            onSelectionChange={onSelectionChange}
            onAssetAdded={onAssetAdded}
            onViewDetails={onViewDetails}
            onDelete={onRowDelete}
          />
          {/* Footer pagination controls (explore-style) */}
          <div className="border-t p-2 mt-2 flex items-center justify-between bg-muted/20 rounded-b-md">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(Math.max(1, pagination.page - 1))}
                disabled={isLoading || pagination.page === 1}
             >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <div className="flex items-center text-sm">
                <span className="mr-2">Page</span>
                <Input
                  className="h-8 w-[60px] text-center"
                  value={pageInputValue}
                  onChange={(e) => setPageInputValue(e.target.value.replace(/[^0-9]/g, ""))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const newPage = Number(pageInputValue);
                      if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
                        goToPage(newPage);
                      } else {
                        setPageInputValue(String(pagination.page));
                      }
                    }
                  }}
                  onBlur={() => {
                    const newPage = Number(pageInputValue);
                    if (!isNaN(newPage) && newPage >= 1 && newPage <= totalPages) {
                      goToPage(newPage);
                    } else {
                      setPageInputValue(String(pagination.page));
                    }
                  }}
                  aria-label="Go to page"
                  disabled={isLoading}
                />
                <span className="mx-2">of {totalPages}</span>
              </div>

              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                onClick={() => goToPage(Math.min(totalPages, pagination.page + 1))}
                disabled={isLoading || pagination.page === totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>

              <div className="flex items-center space-x-4 ml-2">
                <Select value={String(pagination.pageSize)} onValueChange={(v) => changePageSize(Number(v))}>
                  <SelectTrigger className="h-8 w-[120px]">
                    <SelectValue placeholder={`${pagination.pageSize} rows`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25 rows</SelectItem>
                    <SelectItem value="50">50 rows</SelectItem>
                    <SelectItem value="100">100 rows</SelectItem>
                    <SelectItem value="500">500 rows</SelectItem>
                    <SelectItem value="1000">1000 rows</SelectItem>
                  </SelectContent>
                </Select>

                <div className="text-sm text-muted-foreground flex items-center">
                  <span>{totalFilteredCount.toLocaleString()} records</span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 ml-1">
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Total number of assets matching your filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
